// outbox-delivery-worker.ts — F06 (phase-07 tests 17-19, code review H1
// fix): drains pending/uncertain delivery-outbox.ts operations each tick,
// classifying failures via mattermost-retry-classifier.ts.
//
// H1 (code-reviewer-260909-p7-m3.md): a `mattermost_post` op (root or reply
// create) is NEVER safe to blind-retry with a raw `send()` call on an
// AMBIGUOUS failure (5xx / network-lost — the request may have already
// reached the server and committed the post before the response was lost).
// Only a failure PROVEN to have happened before the server could have
// processed the request is safe to retry densely within the same tick:
//   - HTTP 429 (rate-limited — rejected before any handler logic ran), or
//   - a connection-level error before any bytes were written
//     (`ECONNREFUSED`/`ECONNRESET` — the request never left the process).
// Every other retryable-classified failure (5xx, timeout, or any thrown
// error the worker cannot positively attribute to "never sent") is treated
// as AMBIGUOUS: the worker gives `input.send` exactly one more chance to
// reconcile via correlation-marker recovery (a `mattermost_post`-shaped send
// — see mattermost-root-post-manager.ts's `deliverRootPostIntent` — always
// tries `recoverByCorrelationId` before giving up), and if `send` itself
// cannot resolve it, the op is left `uncertain` for a LATER tick to retry
// (never blind-retried again inside this tick). `send` is thus REQUIRED to
// be reconciliation-aware for `mattermost_post` kind ops — this worker
// itself must never call a raw `createPost` a second time; that
// responsibility belongs entirely to mattermost-root-post-manager.ts (test
// "outbox worker composes with root-post-manager reconciliation" in
// outbox-delivery.test.ts locks this down end-to-end, and
// mattermost-root-post-manager-wiring-grep-gate.test.ts locks down that no
// OTHER module calls `createPost` directly). ONE deliberate exception: chat
// replies (kind `mattermost_chat_reply`, mattermost-chat-command-reply.ts) are
// best effort — a duplicate after an ambiguous 5xx is accepted, sends are
// capped instead (review 261006 L11; the grep-gate pins it to that kind).
//
// Auth failures (401/403) are surfaced immediately as `connection_fix_needed`
// after exactly one attempt — never retried densely (spec §7.4). Operations
// left over from a supervisor that exited at a terminal state are ordinary
// backlog: `listPendingOutboxOperations` finds them again on the next run,
// nothing here assumes continuity across runs.

import { listPendingOutboxOperations, updateOutboxStatus } from "./delivery-outbox.js";
import { classifyMattermostResponseStatus } from "./mattermost-retry-classifier.js";

export interface OutboxSendResult {
  remoteId: string;
}

/**
 * `send` for `kind: "mattermost_post"` ops MUST be reconciliation-aware
 * (i.e. backed by `deliverRootPostIntent`, never a raw `createPost`): on an
 * ambiguous failure this worker calls `send` at most ONCE MORE and then
 * stops, trusting `send` itself to have tried correlation-marker recovery
 * before throwing again.
 */
export type OutboxSendFn = (eventId: string) => Promise<OutboxSendResult>;

export interface RunOutboxDeliveryWorkerOnceInput {
  controlRoot: string;
  send: OutboxSendFn;
  maxAttemptsPerTick: number;
  /** Injected in-tick retry backoff base (ms) — §18-adjacent tunable, NOT a
   * hardcoded literal (M1 fix): comes from effective-config's
   * `outbox_retry_backoff_ms`, never a bare number in this file. */
  backoffBaseMs: number;
  /** Backoff sleeper — defaults to a real setTimeout; tests may inject a no-op. */
  sleep?: (ms: number) => Promise<void>;
  now?: () => Date;
}

export interface OutboxDeliveryWorkerRunResult {
  sent: string[];
  retried: string[];
  connectionFixNeeded: string[];
  uncertain: string[];
}

function statusOf(err: unknown): number | undefined {
  return typeof err === "object" && err !== null && "status" in err
    ? (err as { status?: number }).status
    : undefined;
}

/**
 * True ONLY for failures proven to have happened before the server could
 * have processed the request — safe to densely retry with a raw `send()`
 * call because there is no possibility the earlier attempt already
 * committed a post server-side (H1 fix):
 *   - HTTP 429: rejected by rate-limiting before any handler logic ran.
 *   - A connection-level error tagged `preSendSafe` by the caller (e.g. a
 *     `send` wrapper that knows the underlying socket never connected —
 *     `ECONNREFUSED`/`ECONNRESET` before any bytes were written).
 * Every other retryable-classified failure (5xx, timeout, generic network
 * loss) is AMBIGUOUS: the request may have reached the server and the post
 * may already exist, so this worker must not call `send` again blind — it
 * relies on `send` itself (mattermost-root-post-manager.ts's
 * `deliverRootPostIntent` for `mattermost_post` kind) to have already tried
 * correlation-marker reconciliation before throwing.
 */
/** Same proof, from the thrown error alone (chat-reply attempt cap, review 261006 L8). */
export function isProvenPreSendError(err: unknown): boolean {
  return isProvenPreSendFailure(err, statusOf(err));
}

function isProvenPreSendFailure(err: unknown, status: number | undefined): boolean {
  if (status === 429) return true;
  return (
    typeof err === "object" &&
    err !== null &&
    (err as { preSendSafe?: boolean }).preSendSafe === true
  );
}

/** Runs one drain pass over pending/uncertain outbox operations. */
export async function runOutboxDeliveryWorkerOnce(
  input: RunOutboxDeliveryWorkerOnceInput,
): Promise<OutboxDeliveryWorkerRunResult> {
  const sleep = input.sleep ?? ((ms: number) => new Promise((resolve) => setTimeout(resolve, ms)));
  const pending = await listPendingOutboxOperations(input.controlRoot);

  const result: OutboxDeliveryWorkerRunResult = {
    sent: [],
    retried: [],
    connectionFixNeeded: [],
    uncertain: [],
  };

  for (const op of pending) {
    let attempt = 0;
    let lastError: unknown;
    let sent = false;

    while (attempt < input.maxAttemptsPerTick) {
      attempt += 1;
      try {
        const sendResult = await input.send(op.event_id);
        await updateOutboxStatus(
          input.controlRoot,
          op.event_id,
          "sent",
          sendResult.remoteId,
          input.now,
        );
        result.sent.push(op.event_id);
        if (attempt > 1) result.retried.push(op.event_id);
        sent = true;
        break;
      } catch (err) {
        lastError = err;
        const status = statusOf(err);
        const classification =
          status !== undefined ? classifyMattermostResponseStatus(status) : undefined;

        if (classification?.retryClass === "auth_failure") {
          // Exactly one attempt — no dense retry on 401/403.
          result.connectionFixNeeded.push(op.event_id);
          break;
        }
        if (
          classification?.retryClass === "retryable" &&
          attempt < input.maxAttemptsPerTick &&
          isProvenPreSendFailure(err, status)
        ) {
          // Only a PROVEN-pre-send failure (429, or a caller-tagged
          // never-connected error) may retry densely — the earlier attempt
          // could not possibly have committed a post server-side.
          await sleep(input.backoffBaseMs * attempt);
          continue;
        }
        // Ambiguous failure (5xx / network / anything not proven pre-send),
        // or retries exhausted, or not retryable at all — leave as
        // uncertain for a LATER tick to pick back up (backlog preserved,
        // test 19). `send` itself (for mattermost_post kind) already tried
        // correlation-marker reconciliation before this throw reached us —
        // this worker never blind-retries an ambiguous failure (H1 fix).
        break;
      }
    }

    if (!sent && !result.connectionFixNeeded.includes(op.event_id)) {
      result.uncertain.push(op.event_id);
    }
    void lastError;
  }

  return result;
}
